import { ActionFunction } from '../interface/action/action-function.type';
import { findIndex, isArray } from 'lodash';
import { ActionEnum } from '../enum/action.enum';
import { AjaxResponse } from 'rxjs/Rx';
import { IPageData } from '../interface/page/page-data.interface';
import { PageModel } from '../model/page.model';
import { getAllPages } from '../util/action/type/get-all-pages-action.util';
import { action, toJS } from 'mobx';
import { FolderModel } from '../model/media-library/folder.model';
import { ImageModel } from '../model/media-library/image.model';
import { noop } from '../util/action/type/noop-action.util';
import { httpGetMediaLibraryImages } from '../util/action/type/http-get-medialibrary-images-action.util';
import { createImageModelFromData } from '../util/media-library/create-image-model-from-data.util';
import { joinUrl } from '../util/url/join-url.util';
import { convertDesignObjectToServerData } from '../util/page/convert-page-to-server-data.util';
import { manageEditor } from '../util/action/type/manage-editor-action.util';
import { DesignTypeEnum } from '../enum/design-type.enum';
import { getDesignObjectMetadata } from '../util/action/type/get-design-object-metadata-action.util';
import { Observable } from 'rxjs/Observable';
import { addGlobalError } from '../util/action/type/add-global-error.util';
import { deleteDesignObjectDraft } from '../util/action/type/delete-design-object-draft-action.util';
import { getPageUniqueID } from '../util/page/get-page-unique-id.util';
import {
  IFilterGroupsHolder,
  IUsedDesignObjectMentionMetadata,
} from '../interface/page/design-object-mention-metadata.interface';
import { findLinkedData } from '../util/page/find-linked-data.util';
import { ColorModel } from '../model/settings/color.model';
import { getCDNUrl } from '../util/url/get-cdn-url.util';
import { ISubmitFormErrorMessages } from '../util/action/type/submit-form-action.util';
import { CmsApiErrorCodes } from '../enum/cms-api-error-codes.enum';
import { AjaxError } from 'rxjs/observable/dom/AjaxObservable';
import { ISettingsRootFile } from '../interface/settings.interface';
import { invariant } from '../util/debug/invariant.util';
import { IDesignObjectData } from '../interface/design-object-data.interface';
import { AppStateModel } from '../model/app-state.model';
import { getTemplateMetadata } from '../util/action/type/get-template-metadata-action.util';
import { EmailModel } from '../model/email.model';
import { WebDocumentModel } from '../model/web-document.model';
import { LinkedinLoginTypeEnum } from '../enum/linkedin-login-type.enum';
import { getTranslation } from '../util/translation/get-translation';
import { redirectPage } from '../util/action/type/redirect-page.util';
import { isCustomListModel } from '../util/design-object/is-custom-list-model.util';
import { getPageURI } from '../util/url/get-page-uri.util';
import { draftToString } from '../util/draft/draft-to-string.util';
import { createLinkedDataFromData } from '../util/linked-data/create-linked-data-from-data';
import { resolveLinkedData } from '../util/action/type/get-linked-data-with-filter-action.util';
import { isServerSide } from '../util/server-side-rendering/is-server-side';
import { findLinkedDataAsyncDataToResolve } from '../util/linked-data/find-linked-data-async-data-to-resolve.util';
import { ILinkedDataPagedProperties } from '../interface/linked-data-paged-properties.interface';
import { IFileData } from '../component/blocks/form/fields/file-field';
import { isomorphicAjax } from '../util/service/isomorphix-ajax.util';
import { AjaxRequestMethodEnum } from '../enum/ajax-request-method.enum';
import { logDebugEvent } from '../util/debug/log-debug-event.util';
import { JobadViewSortEnum } from '../enum/block/jobad-view-sort.enum';
import { GoogleTagManager } from '../google-tag-manager';

export interface IGetPageBySlugPayload {
  slug: string;
  type: DesignTypeEnum;
  filterParameters: any;
  useCache: boolean;
}

export interface IGetHomePagePayload {
  type: DesignTypeEnum;
  filterParameters: any;
  useCache: boolean;
}

export interface IHttpSavePagePayload {
  content?: PageModel;
}

export interface IHttpUpdatePagePayload {
  content?: PageModel;
  pageId: string;
}

export interface IHttpDeletePagePayload {
  pageId: string;
}

export interface IHttpGetTemplateById {
  templateId: string;
}

export const getTemplateById$: ActionFunction<IHttpGetTemplateById> = (
  action$,
  { state, service, logger },
) => {
  return action$.type(ActionEnum.GET_TEMPLATE_BY_ID).switchMap(
    action((payload: IHttpGetTemplateById) => {
      state.isLoading = true;
      logDebugEvent(logger, 'CMS-SSR GET_TEMPLATE_BY_ID entry');
      return service.templateService.getTemplateDesignById(payload.templateId).map(
        action((response: AjaxResponse) => {
          const designObjectData = response.response as IDesignObjectData;
          state.activeDesign.setDesignData(designObjectData, state.configuration.inAdmin);
          state.activeDesign.templateId = payload.templateId;
          state.activeDesign.lastDataSyncState = response.response;
          if (state.configuration.inAdmin) {
            return getTemplateMetadata(payload.templateId);
          }
          state.isLoading = false;
          logDebugEvent(logger, 'CMS-SSR GET_TEMPLATE_BY_ID end');
          return noop();
        }),
      );
    }),
  );
};

export interface IHttpGetWebDocByHash {
  templateId: string;
  hash: string;
}

export const getWebDocByHash$: ActionFunction<IHttpGetWebDocByHash> = (
  action$,
  { state, service },
) => {
  return action$.type(ActionEnum.GET_WEB_DOC_BY_HASH).switchMap(
    action((payload: IHttpGetWebDocByHash) => {
      state.isLoading = true;
      return service.templateService.getWebDocByHash(payload.templateId, payload.hash).map(
        action((response: AjaxResponse) => {
          const designObjectData = response.response as IDesignObjectData;
          state.activeDesign.setDesignData(designObjectData, state.configuration.inAdmin);
          state.activeDesign.templateId = payload.templateId;
          state.activeDesign.lastDataSyncState = response.response;
          state.isLoading = false;
          return noop();
        }),
      );
    }),
  );
};

const processMetadata = (state: AppStateModel) =>
  action((metadataResponse: AjaxResponse) => {
    state.editor.possibleMentions = metadataResponse.response;
    return manageEditor();
  });

export const getTemplateMetadata$: ActionFunction<IHttpGetTemplateById> = (
  action$,
  { state, service },
) => {
  return action$
    .type(ActionEnum.GET_TEMPLATE_METADATA)
    .switchMap((payload: IHttpGetTemplateById) => {
      return service.templateService
        .getTemplateMetadata(payload.templateId)
        .map(processMetadata(state));
    });
};

const processPage = action(
  (state: AppStateModel, filterParameters: any, response: AjaxResponse) => {
    state.activeDesign.setDesignData(
      response.response ? (response.response as IPageData) : null,
      state.configuration.inAdmin,
    );
    state.activeDesign.lastDataSyncState = response.response;
    if (state.configuration.inAdmin) {
      return getDesignObjectMetadata(
        state.activeDesign.data.type,
        isCustomListModel(state.activeDesign.data)
          ? state.activeDesign.data.customListDefinitionId
          : undefined,
      );
    }
    state.isLoading = false;
    setTimeout(() => {
      if (state.isBackwards && state.pagesScrollLocation[state.activeDesign.data.id]) {
        window.scrollTo({
          top: state.pagesScrollLocation[state.activeDesign.data.id],
          behavior: 'smooth',
        });
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
      state.isBackwards = false;
    }, 200);
    const mentionsToResolve: IUsedDesignObjectMentionMetadata[] = findLinkedDataAsyncDataToResolve(
      state.activeDesign.data.linkedData,
    );
    GoogleTagManager.pushCustomPageViewEvent(state, '200');
    if (mentionsToResolve.length === 0 || isServerSide() || state.configuration.inAdmin) {
      return noop();
    } else {
      return resolveLinkedData(mentionsToResolve, true, { pagedProperties: { page: 1 } });
    }
  },
);

export const getPageBySlug$: ActionFunction<IGetPageBySlugPayload> = (
  action$,
  { state, service },
) => {
  return action$.type(ActionEnum.GET_PAGE_BY_SLUG).switchMap(
    action((payload: IGetPageBySlugPayload) => {
      let slug = payload.slug;
      if (!slug || slug === '') {
        slug = '/';
      }
      state.isLoading = true;
      return (
        service.page
          .getPageBySlug(slug, payload.type, payload.filterParameters, payload.useCache)
          // TODO2024 - remove any
          .map((response: any) => processPage(state, payload.filterParameters, response))
      );
    }),
  );
};

export const getHomePage$: ActionFunction<IGetHomePagePayload> = (action$, { state, service }) => {
  return action$.type(ActionEnum.GET_HOME_PAGE).switchMap(
    action((payload: IGetHomePagePayload) => {
      state.isLoading = true;
      return (
        service.page
          .getHomePage(payload.type, payload.filterParameters, payload.useCache)
          // TODO2024 - remove any
          .map((response: any) => processPage(state, payload.filterParameters, response))
      );
    }),
  );
};

export interface IGetPageTypeMetadataPayload {
  type: DesignTypeEnum;
  definitionId?: string;
}

export const getDesignObjectMetadata$: ActionFunction<IGetPageTypeMetadataPayload> = (
  action$,
  { state, service },
) => {
  return action$
    .type(ActionEnum.HTTP_GET_DESIGN_OBJECT_METADATA)
    .switchMap((payload: IGetPageTypeMetadataPayload) => {
      if (payload.type === DesignTypeEnum.Page) {
        return service.page.getMetadata().map(processMetadata(state));
      } else if (payload.type === DesignTypeEnum.JobAdPage) {
        return service.jobads.getMetadata().map(processMetadata(state));
      } else if (payload.type === DesignTypeEnum.CustomList) {
        if (payload.definitionId) {
          return service.customListDefinition
            .getMetadata(payload.definitionId)
            .map(processMetadata(state));
        } else {
          return Observable.of(noop());
        }
      } else if (
        payload.type === DesignTypeEnum.Email ||
        payload.type === DesignTypeEnum.WebDocument
      ) {
        return Observable.of(getTemplateMetadata(state.activeDesign.templateId));
      }
      invariant(false, 'Unknown type for metadata', payload.type);
    });
};

export const httpWarmUpCache$: ActionFunction<IHttpSavePagePayload> = (
  action$,
  { state, service },
) => {
  return action$.type(ActionEnum.HTTP_WARM_UP_CACHE).switchMap(
    action(() => {
      state.isLoading = true;
      return service.maintainanceService
        .warmupCache()
        .switchMap(() => {
          return isomorphicAjax({
            url: joinUrl(state.configuration.CMSUrl, 'warmup-cache'),
            method: AjaxRequestMethodEnum.GET,
            crossDomain: true,
          });
        })
        .switchMap(() => Observable.of([noop()]))
        .finally(action(() => (state.isLoading = false)));
    }),
  );
};

export const ping$: ActionFunction = (action$, { state, service }) => {
  return action$
    .type(ActionEnum.PING)
    .switchMap(() => {
      state.pinging = true;
      return service.maintainanceService.ping().end(
        action((response: AjaxResponse) => {
          state.hasInternetConnection = response.status === 200;
        }),
      );
    })
    .finally(() => {
      state.pinging = false;
    });
};
export const httpSavePage$: ActionFunction<IHttpSavePagePayload> = (
  action$,
  { state, service },
) => {
  return action$
    .type(ActionEnum.HTTP_SAVE_PAGE)
    .switchMap(
      action((payload: IHttpSavePagePayload) => {
        state.isLoading = true;
        return service.page
          .createPage(JSON.stringify(convertDesignObjectToServerData(payload.content)))
          .map(
            action((response: AjaxResponse) => {
              state.isLoading = false;
              state.activeDesign.setDesignData(payload.content, state.configuration.inAdmin);
              state.activeDesign.lastDataSyncState = payload.content;
              state.activeDesign.data.linkedData = response.response.linkedData;
              const returnActions = [getAllPages()];
              const page = state.activeDesign.data as PageModel;
              returnActions.push(
                redirectPage(
                  joinUrl(
                    '/admin/pages/',
                    getPageURI(
                      Object.assign({}, page, { title: draftToString(page.title, state) }),
                    ),
                  ),
                ),
              );
              return returnActions;
            }),
          );
      }),
    )
    .catch((e) => {
      return Observable.throw(
        addGlobalError(
          getTranslation(state.configuration, 'page_update.error_header'),
          getTranslation(state.configuration, 'page_update.error_body', [e.message]),
        ),
      );
    });
};

export const httpUpdatePage$: ActionFunction<IHttpUpdatePagePayload> = (
  action$,
  { state, service, history },
) => {
  return action$.type(ActionEnum.HTTP_UPDATE_PAGE).switchMap(
    action((payload: IHttpUpdatePagePayload) => {
      state.isLoading = true;
      return service.page
        .updatePage(
          payload.pageId,
          JSON.stringify(convertDesignObjectToServerData(payload.content)),
        )
        .map(
          action((response: AjaxResponse) => {
            state.isLoading = false;
            state.activeDesign.data.dateUpdated = response.response.dateUpdated;
            const returnActions = [getAllPages()];
            const page = state.activeDesign.data as PageModel;
            returnActions.push(
              redirectPage(
                joinUrl(
                  '/admin/pages/',
                  getPageURI(Object.assign({}, page, { title: draftToString(page.title, state) })),
                ),
              ),
            );
            return returnActions;
          }),
        )
        .catch((e) => {
          return Observable.throw(
            addGlobalError(
              getTranslation(state.configuration, 'page_update.error_header'),
              getTranslation(state.configuration, 'page_update.error_body', [e.message]),
            ),
          );
        })
        .end(action(() => (state.activeDesign.lastDataSyncState = payload.content)));
    }),
  );
};

export interface IHttpUpdateTemplatePayload {
  content?: EmailModel | WebDocumentModel;
  templateId: string;
}

export const httpUpdateTemplate$: ActionFunction<IHttpUpdateTemplatePayload> = (
  action$,
  { state, service },
) => {
  return action$.type(ActionEnum.HTTP_UPDATE_TEMPLATE).switchMap(
    action((payload: IHttpUpdateTemplatePayload) => {
      state.isLoading = true;
      return service.templateService
        .updateTemplateById(
          payload.templateId,
          JSON.stringify(convertDesignObjectToServerData(payload.content)),
        )
        .end(
          action((response: AjaxResponse) => {
            state.isLoading = false;
            state.activeDesign.lastDataSyncState = payload.content;
          }),
        );
    }),
  );
};

export const httpDeletePage$: ActionFunction<IHttpDeletePagePayload> = (
  action$,
  { state, history, service },
) => {
  return action$.type(ActionEnum.HTTP_DELETE_PAGE).switchMap(
    action((payload: IHttpDeletePagePayload) => {
      return service.page
        .deletePage({
          pageId: payload.pageId,
        })
        .map(
          action((response: AjaxResponse) => {
            if (response.status === 200) {
              const index = findIndex(state.allPages, ({ id }) => id === payload.pageId);
              if (index !== -1) {
                const removed = state.allPages.splice(index, 1)[0];
                return [
                  redirectPage('/admin/pages'),
                  deleteDesignObjectDraft(getPageUniqueID(removed)),
                ];
              }
            }
            return noop();
          }),
        );
    }),
  );
};

export const getAllPages$: ActionFunction = (action$, { state, service, logger }) => {
  return action$.type(ActionEnum.GET_ALL_PAGES).switchMap(() => {
    logDebugEvent(logger, 'CMS-SSR GET_ALL_PAGES entry');
    return service.page.getAllPages().end(
      action(({ response }) => {
        logDebugEvent(logger, 'CMS-SSR GET_ALL_PAGES end');
        if (
          !state.configuration.inAdmin ||
          state.configuration.authorization.cmsAdvancedContentUse
        ) {
          state.allPages = response;
        } else {
          state.allPages = (response || []).filter(
            (value) => value.type !== DesignTypeEnum.CustomList,
          );
        }
      }),
    );
  });
};

export interface IHttpUploadImagePayload {
  image: ImageData;
  folderId: string;
}

export const httpUploadImage$: ActionFunction<IHttpUploadImagePayload> = (
  action$,
  { state, service },
) => {
  return action$.type(ActionEnum.HTTP_UPLOAD_IMAGE).switchMap((payload) => {
    return service.mediaLibrary.uploadImage(payload.folderId, payload.image).end(
      action((response: AjaxResponse) => {
        const folderIndex = findIndex(state.mediaLibrary.folders, { id: payload.folderId });
        if (folderIndex !== -1) {
          state.mediaLibrary.folders[folderIndex].images.push(
            createImageModelFromData(response.response),
          );
        }
        state.mediaLibrary.isUploading = false;
      }),
    );
  });
};

export interface IHttpUploadFilePayload {
  file: File;
}

export const httpUploadFile$: ActionFunction<IHttpUploadFilePayload> = (
  action$,
  { state, service },
) => {
  return action$.type(ActionEnum.HTTP_UPLOAD_FILE).switchMap(
    action((payload: IHttpUploadFilePayload) => {
      state.isLoading = true;
      return service.mediaLibrary.uploadFile(payload.file).end(
        action((response: AjaxResponse) => {
          if (response && response.response) {
            state.adminSettings.rootFiles.push((response.response as ISettingsRootFile[]).pop());
          }
          state.isLoading = false;
        }),
      );
    }),
  );
};

export interface IHttpCreateFolderPayload {
  name: string;
}

export const httpCreateFolder$: ActionFunction<IHttpCreateFolderPayload> = (
  action$,
  { state, service },
) => {
  return action$
    .type(ActionEnum.HTTP_CREATE_FOLDER)
    .switchMap((payload: IHttpCreateFolderPayload) => {
      return service.mediaLibrary.createFolder(payload.name).end((ajaxResponse: AjaxResponse) => {
        if (ajaxResponse.status === 200) {
          const folder = new FolderModel();
          folder.name = ajaxResponse.response.name;
          folder.id = ajaxResponse.response.id;
          state.mediaLibrary.appendFolder(folder);
        }
      });
    });
};

export interface IHttpDeleteFolderPayload {
  id: string;
}

export const httpDeleteFolder$: ActionFunction<IHttpDeleteFolderPayload> = (
  action$,
  { state, service },
) => {
  return action$
    .type(ActionEnum.HTTP_DELETE_FOLDER)
    .switchMap((payload: IHttpDeleteFolderPayload) => {
      return service.mediaLibrary.deleteFolder(payload.id).end((ajaxResponse: AjaxResponse) => {
        if (ajaxResponse.status === 200) {
          state.mediaLibrary.deleteFolder(payload.id);
        }
      });
    });
};

export interface IHttpDeleteImagePayload {
  id: string;
  folderId: string;
}

export const httpDeleteImage$: ActionFunction<IHttpDeleteImagePayload> = (
  action$,
  { state, service },
) => {
  return action$
    .type(ActionEnum.HTTP_DELETE_IMAGE)
    .switchMap((payload: IHttpDeleteImagePayload) => {
      return service.mediaLibrary.deleteImage(payload.id).end((ajaxResponse: AjaxResponse) => {
        if (ajaxResponse.status === 200) {
          const folderIndex = findIndex(state.mediaLibrary.folders, { id: payload.folderId });
          if (folderIndex !== -1) {
            const imageIndex = findIndex(state.mediaLibrary.folders[folderIndex].images, {
              id: payload.id,
            });
            if (imageIndex !== -1) {
              state.mediaLibrary.folders[folderIndex].deleteImage(imageIndex);
            }
          }
        }
      });
    });
};

export interface IHttpMoveImagePayload {
  imageId: string;
  folderId: string;
  oldFolderId: string;
}

export const httpMoveImage$: ActionFunction<IHttpMoveImagePayload> = (
  action$,
  { state, service },
) => {
  return action$.type(ActionEnum.HTTP_MOVE_IMAGE).switchMap((payload: IHttpMoveImagePayload) => {
    return service.mediaLibrary
      .moveImage(payload.imageId, payload.folderId)
      .end((ajaxResponse: AjaxResponse) => {
        if (ajaxResponse.status === 200) {
          const folderIndex = findIndex(state.mediaLibrary.folders, { id: payload.oldFolderId });
          if (folderIndex !== -1) {
            const imageIndex = findIndex(state.mediaLibrary.folders[folderIndex].images, {
              id: payload.imageId,
            });
            if (imageIndex !== -1) {
              state.mediaLibrary.folders[folderIndex].deleteImage(imageIndex);
            }
          }
        }
      });
  });
};

export interface IHttpDeleteFilePayload {
  id: string;
}

export const httpDeleteFile$: ActionFunction<IHttpDeleteFilePayload> = (
  action$,
  { state, service },
) => {
  return action$.type(ActionEnum.HTTP_DELETE_FILE).switchMap(
    action((payload: IHttpDeleteImagePayload) => {
      state.isLoading = true;
      return service.mediaLibrary.deleteFile(payload.id).end(
        action(() => {
          const deleteIndex = findIndex(
            state.adminSettings.rootFiles,
            (file: ISettingsRootFile) => file.id === payload.id,
          );
          if (deleteIndex !== -1) {
            state.adminSettings.rootFiles.splice(deleteIndex, 1);
          }
          state.isLoading = false;
        }),
      );
    }),
  );
};

export interface IHttpMediaLibraryRenamePayload {
  id: string;
  name: string;
}

export const httpMediaLibraryRename$: ActionFunction<IHttpMediaLibraryRenamePayload> = (
  action$,
  { state, service },
) => {
  return action$
    .type(ActionEnum.HTTP_MEDIALIBRARY_RENAME)
    .switchMap((payload: IHttpMediaLibraryRenamePayload) => {
      return service.mediaLibrary.rename(payload.id, payload.name).end(
        action((ajaxResponse: AjaxResponse) => {
          if (ajaxResponse.status === 200) {
            const folderIndex = findIndex(state.mediaLibrary.folders, { id: payload.id });
            state.mediaLibrary.folders[folderIndex].name = payload.name;
          }
        }),
      );
    });
};

export interface IHttpRenameImagePayload {
  image: ImageModel;
  name: string;
}

export const httpRenameImage$: ActionFunction<IHttpRenameImagePayload> = (
  action$,
  { state, service },
) => {
  return action$
    .type(ActionEnum.HTTP_RENAME_IMAGE)
    .switchMap((payload: IHttpRenameImagePayload) => {
      return service.mediaLibrary.rename(payload.image.id, payload.name).end(
        action((ajaxResponse: AjaxResponse) => {
          if (ajaxResponse.status === 200) {
            payload.image.name = payload.name;
          }
        }),
      );
    });
};

export interface IHttpGetMediaLibraryImagesPayload {
  folderId: string;
}

export const httpGetMediaLibraryImages$: ActionFunction<IHttpGetMediaLibraryImagesPayload> = (
  action$,
  { state, service },
) => {
  return action$.type(ActionEnum.HTTP_GET_MEDIALIBRARY_IMAGES).switchMap((payload) => {
    return service.mediaLibrary.getMediaLibraryImages(payload.folderId).end(
      action((response: AjaxResponse) => {
        if (response.status === 200) {
          const imagesFromServer: ImageModel[] = response.response.map((file) =>
            createImageModelFromData(file),
          );
          const folderIndex = findIndex(state.mediaLibrary.folders, { id: payload.folderId });
          if (folderIndex !== -1) {
            state.mediaLibrary.folders[folderIndex].images = imagesFromServer;
          }
        }
      }),
    );
  });
};

export const httpGetMedialibraryFolders$: ActionFunction = (action$, { state, service }) => {
  return action$.type(ActionEnum.HTTP_GET_MEDIALIBRARY_FOLDERS).switchMap(() => {
    return service.mediaLibrary.getMediaLibraryFolders().map(
      action((response: AjaxResponse) => {
        if (response.status === 200) {
          state.mediaLibrary.folders = response.response.directories.map((directory) => {
            const folder = new FolderModel();
            folder.id = directory.id;
            folder.name = directory.name;
            folder.deleted = directory.deleted;
            return folder;
          });
          if (state.mediaLibrary.folders.length > 0) {
            return httpGetMediaLibraryImages(state.mediaLibrary.folders[0].id);
          }
        }
        return noop();
      }),
    );
  });
};

export interface ISubmitFormPayload {
  form: { fields: { [key: string]: any }; files: { [key: string]: IFileData } };
  errorMessages: ISubmitFormErrorMessages;
  queryData: any;
  url: string;
  urlParams: { [key: string]: string };
}

export const submitForm$: ActionFunction<ISubmitFormPayload> = (action$, { state, service }) => {
  return action$.type(ActionEnum.SUBMIT_FORM).switchMap(
    action((payload: ISubmitFormPayload) => {
      state.isLoading = true;
      return service.formSubmitService
        .submit(payload.url, payload.form, payload.queryData, payload.urlParams)
        .catch(
          action((error: AjaxError) => {
            state.isLoading = false;
            if (!error.response) {
              return Observable.throw(addGlobalError('', `${error.name}\n${error.message}`));
            }
            if (error.response.errorCode === CmsApiErrorCodes.SameEmail) {
              return Observable.throw(
                addGlobalError(
                  payload.errorMessages.alreadyAppliedErrorTitle,
                  payload.errorMessages.alreadyAppliedErrorBody,
                ),
              );
            }
            if (error.response.errorCode === CmsApiErrorCodes.InvalidFileFormat) {
              return Observable.throw(
                addGlobalError(
                  payload.errorMessages.invalidFileFormatTitle,
                  payload.errorMessages.invalidFileFormatBody,
                ),
              );
            }
            if (error.response.errorCode === CmsApiErrorCodes.EmptyFile) {
              return Observable.throw(
                addGlobalError(
                  payload.errorMessages.emptyFileUploadedErrorMessageTitle,
                  payload.errorMessages.emptyFileUploadedErrorMessageBody,
                ),
              );
            }
            return Observable.throw(
              addGlobalError(
                '',
                `${payload.errorMessages.serverErrorTitle}\n${payload.errorMessages.serverErrorBody}`,
              ),
            );
          }),
        )
        .end(
          action(() => {
            state.isLoading = false;
            return true;
          }),
        );
    }),
  );
};

export interface IResolveLinkedDataPayload {
  usedMetadata: IUsedDesignObjectMentionMetadata[];
  filters?: IFilterGroupsHolder;
  pagedProperties?: Partial<ILinkedDataPagedProperties>;
  sortBy?: JobadViewSortEnum;
  replaceData: boolean;
}

// TODO2024 - remove @ts-ignore
// @ts-ignore
export const resolveLinkedData$: ActionFunction<IResolveLinkedDataPayload> = (
  action$,
  { state, service },
) => {
  return action$
    .type(ActionEnum.RESOLVE_LINKED_DATA)
    .switchMap((payload: IResolveLinkedDataPayload) => {
      const usedMetadata = payload.usedMetadata.filter((md) =>
        Boolean(findLinkedData(md, state.activeDesign.data)),
      );
      const metadata = usedMetadata.map((currentMetadata) => {
        // TODO2024 - remove any
        const toReturn: any = Object.assign(
          {},
          toJS(findLinkedData(currentMetadata, state.activeDesign.data)),
          { _data: undefined, data: undefined },
        );
        if (payload.filters) {
          toReturn.filters = payload.filters;
        }
        if (payload.pagedProperties && toReturn.pagedProperties) {
          toReturn.pagedProperties = Object.assign(
            {},
            toReturn.pagedProperties,
            payload.pagedProperties,
          );
        }
        if (payload.sortBy) {
          toReturn.sortBy = payload.sortBy;
        }
        return toReturn;
      });
      return service.linkedDataResolverService
        .resolveData(
          metadata,
          state.activeDesign.data.id,
          state.activeDesign.loadParams.filterParameters,
        )
        .do((result: AjaxResponse) => {
          if (!result.response) {
            return;
          }
          usedMetadata.forEach(
            action((currentMetadata, index) => {
              const currentResponse = result.response[index];
              if (!currentResponse) {
                return 'continue';
              }
              const foundData = findLinkedData(currentMetadata, state.activeDesign.data);
              if (!foundData) {
                return 'continue';
              }
              if (payload.replaceData) {
                const dataIndex = state.activeDesign.data.linkedData.indexOf(foundData);
                state.activeDesign.data.linkedData.splice(
                  dataIndex,
                  1,
                  createLinkedDataFromData(currentResponse),
                );
              } else if (
                currentResponse.pagedProperties &&
                foundData.pagedProperties &&
                foundData.data &&
                isArray(toJS(foundData.data))
              ) {
                if (foundData.pagedProperties.page + 1 === currentResponse.pagedProperties.page) {
                  foundData.data = foundData.data.concat(currentResponse.data);
                  foundData.pagedProperties = currentResponse.pagedProperties;
                }
              }
            }),
          );
          state.activeDesign.data.linkedData = state.activeDesign.data.linkedData.slice();
        });
    });
};

// TODO2024 - remove @ts-ignore
// @ts-ignore
export const httpUpdateFonts$: ActionFunction = (action$, { state, service }) => {
  return action$.type(ActionEnum.HTTP_UPDATE_FONTS).switchMap(() => {
    return service.siteService.updateFonts(state.fonts.getAllRawFonts());
  });
};

// TODO2024 - remove @ts-ignore
// @ts-ignore
export const httpUpdateLogos$: ActionFunction = (action$, { state, service }) => {
  return action$.type(ActionEnum.HTTP_UPDATE_LOGOS).switchMap(() => {
    return service.siteService.updateLogos([state.logos.white, state.logos.dark]);
  });
};

// TODO2024 - remove @ts-ignore
// @ts-ignore
export const httpUpdateColors$: ActionFunction = (action$, { state, service }) => {
  return action$.type(ActionEnum.HTTP_UPDATE_COLORS).switchMap(() => {
    return service.siteService.updateColors(state.colors.getAllRawColors()).do(
      action(() => {
        state.configuration.settings.colors.forEach((color: ColorModel) => (color.isSaved = true));
      }),
    );
  });
};

export const httpGetLinkedInSettings$: ActionFunction = (action$, { state, service }) => {
  return action$.type(ActionEnum.HTTP_GET_LINKED_IN_SETTINGS).switchMap(() => {
    return service.siteService.getLinkedInSettings().map(
      action((response: AjaxResponse) => {
        if (response.status === 200) {
          if (response.response) {
            state.adminSettings = Object.assign({}, state.adminSettings, {
              linkedInClientId: response.response.clientId,
              linkedInClientSecret: response.response.clientSecret,
            });
          }
        }
        return noop();
      }),
    );
  });
};

export interface IHttpUpdateAdminSettingsPayload {
  type: LinkedinLoginTypeEnum;
  clientId: string;
  clientSecret: string;
}

export const httpUpdateAdminSettings$: ActionFunction<IHttpUpdateAdminSettingsPayload> = (
  action$,
  { state, service },
) => {
  return action$
    .type(ActionEnum.HTTP_UPDATE_ADMIN_SETTINGS)
    .switchMap((payload) => {
      return service.siteService
        .updateLinkedInSettings(payload.type, payload.clientId, payload.clientSecret)
        .map(
          action(() => {
            state.adminSettings.linkedInLoginType = payload.type;
            state.adminSettings.linkedInClientId = payload.clientId;
            state.adminSettings.linkedInClientSecret = payload.clientSecret;
            return true;
          }),
        )
        .catch(() => {
          return Observable.of(false);
        });
    })
    .end((isSuccessful) => isSuccessful);
};

export interface IHttpUpdateAdminSettingsPayload {
  type: LinkedinLoginTypeEnum;
  clientId: string;
  clientSecret: string;
}

export interface IHttpUpdateTagManagerSettingsPayload {
  tagManagerId: string;
  trackCustomEvents: boolean;
  countryCode: string;
  language: string;
  bookingEngine: string;
  businessArea: string;
}

export const httpUpdateTagManagerSettings$: ActionFunction<IHttpUpdateTagManagerSettingsPayload> = (
  action$,
  { state, service },
) => {
  return action$
    .type(ActionEnum.HTTP_UPDATE_TAG_MANAGER_SETTINGS)
    .switchMap((payload) => {
      return service.siteService
        .updateTagManagerSettings(
          payload.tagManagerId,
          payload.trackCustomEvents,
          payload.countryCode,
          payload.language,
          payload.bookingEngine,
          payload.businessArea,
        )
        .map(
          action(() => {
            state.adminSettings.tagManagerId = payload.tagManagerId;
            state.adminSettings.tagManagerTrackCustomEvents = payload.trackCustomEvents;
            state.adminSettings.tagManagerCountryCode = payload.countryCode;
            state.adminSettings.tagManagerLanguage = payload.language;
            state.adminSettings.tagManagerBookingEngine = payload.bookingEngine;
            state.adminSettings.tagManagerBusinessArea = payload.businessArea;
            return true;
          }),
        )
        .catch(() => {
          return Observable.of(false);
        });
    })
    .end((isSuccessful) => isSuccessful);
};

export interface IHttpUploadFavIconPayload {
  image: ImageData;
}

export const httpUploadFavIcon$: ActionFunction<IHttpUploadFavIconPayload> = (
  action$,
  { state, service },
) => {
  return action$.type(ActionEnum.HTTP_UPLOAD_FAV_ICON).switchMap((payload) => {
    return service.siteService.uploadFavIcon(payload.image).end(
      action((response: AjaxResponse) => {
        if (response.response) {
          state.adminSettings.faviconLocation = getCDNUrl(
            state.configuration,
            response.response.faviconLocation,
          );
        }
      }),
    );
  });
};

// TODO2024 - remove @ts-ignore
// @ts-ignore
export const httpUpdateCookiePolicySettings$: ActionFunction = (action$, { state, service }) => {
  return action$.type(ActionEnum.HTTP_UPDATE_COOKIE_POLICY_SETTINGS).switchMap(() => {
    return service.siteService.updateCookiePolicySettings({
      cookieWarningLink: state.configuration.settings.cookiePolicy.cookieWarningLink,
      cookieWarningOpacity: state.configuration.settings.cookiePolicy.cookieWarningOpacity,
      cookieWarningText: state.configuration.settings.cookiePolicy.cookieWarningText,
      cookieWarningFontColor: state.configuration.settings.cookiePolicy.cookieWarningFontColor,
      cookieWarningBackgroundColor:
        state.configuration.settings.cookiePolicy.cookieWarningBackgroundColor,
      cookieWarningButtonText: state.configuration.settings.cookiePolicy.cookieWarningButtonText,
      cookieWarningButtonColor: state.configuration.settings.cookiePolicy.cookieWarningButtonColor,
    });
  });
};

// TODO2024 - remove @ts-ignore
// @ts-ignore
export const httpUpdateSpinnerSettings$: ActionFunction = (action$, { state, service }) => {
  return action$.type(ActionEnum.HTTP_UPDATE_SPINNER_SETTINGS).switchMap(() => {
    return service.siteService.updateSpinnerSettings(
      state.configuration.settings.spinnerConfig.spinner,
      state.configuration.settings.spinnerConfig.spinnerColor,
    );
  });
};

// TODO2024 - remove @ts-ignore
// @ts-ignore
export const httpUpdateMainMenuSettings$: ActionFunction = (action$, { state, service }) => {
  return action$.type(ActionEnum.HTTP_UPDATE_MAIN_MENU_SETTINGS).switchMap(() => {
    return service.siteService.updateMainMenuSettings(toJS(state.configuration.settings.mainMenu));
  });
};

export interface IHttpUpdateGlobalErrorSettingsPayload {
  errorMessageTitleText: string;
  noInternetConnectionTitleText: string;
  errorMessageJobAdExpiredText: string;
  errorMessageJobAdNotPublishedText: string;
  errorMessageIeWarning: string;
}

export const httpUpdateGlobalErrorSettings$: ActionFunction<
  IHttpUpdateGlobalErrorSettingsPayload
> = (action$, { state, service }) => {
  return action$
    .type(ActionEnum.HTTP_UPDATE_GLOBAL_ERROR_SETTINGS)
    .switchMap((payload) => {
      return service.siteService
        .updateGlobalErrorSettings(
          payload.errorMessageTitleText,
          payload.noInternetConnectionTitleText,
          payload.errorMessageJobAdExpiredText,
          payload.errorMessageJobAdNotPublishedText,
          payload.errorMessageIeWarning,
        )
        .map(
          action(() => {
            state.adminSettings.errorMessageTitleText = payload.errorMessageTitleText;
            state.adminSettings.noInternetConnectionTitleText =
              payload.noInternetConnectionTitleText;
            state.adminSettings.errorMessageJobAdExpiredText = payload.errorMessageJobAdExpiredText;
            state.adminSettings.errorMessageJobAdNotPublishedText =
              payload.errorMessageJobAdNotPublishedText;
            state.adminSettings.errorMessageIeWarning = payload.errorMessageIeWarning;
            return true;
          }),
        )
        .catch(() => {
          return Observable.of(false);
        });
    })
    .end((isSuccessful) => isSuccessful);
};

export interface IHttpUpdateCookiePoliciesPayload {
  enableIntelliplanCookiePolicy: boolean;
  enableOneTrustCookiePolicy: boolean;
  oneTrustAccountId: string;
  enableCookieInformationPolicy: boolean;
  cookieInformationLanguage: string;
}

export const httpUpdateCookiePolicies$: ActionFunction<IHttpUpdateCookiePoliciesPayload> = (
  action$,
  { state, service },
) => {
  return action$
    .type(ActionEnum.HTTP_UPDATE_COOKIE_POLICIES_SETTINGS)
    .switchMap(
      action((payload: IHttpUpdateCookiePoliciesPayload) => {
        state.isLoading = true;
        return service.siteService
          .updateCookiePolicies(
            payload.enableIntelliplanCookiePolicy,
            payload.enableOneTrustCookiePolicy,
            payload.oneTrustAccountId,
            payload.enableCookieInformationPolicy,
            payload.cookieInformationLanguage,
          )
          .map(
            action(() => {
              state.adminSettings.enableIntelliplanCookiePolicy =
                payload.enableIntelliplanCookiePolicy;
              state.adminSettings.enableOneTrustCookiePolicy = payload.enableOneTrustCookiePolicy;
              state.adminSettings.oneTrustAccountId = payload.oneTrustAccountId;
              state.adminSettings.enableCookieInformationPolicy =
                payload.enableCookieInformationPolicy;
              state.adminSettings.cookieInformationLanguage = payload.cookieInformationLanguage;
              return true;
            }),
          )
          .catch(() => {
            return Observable.of(false);
          })
          .finally(
            action(() => {
              state.isLoading = false;
            }),
          );
      }),
    )
    .end((isSuccessful) => isSuccessful);
};

// TODO2024 - remove @ts-ignore
// @ts-ignore
export const httpUpdateButtonThemeSettings$: ActionFunction = (action$, { state, service }) => {
  return action$.type(ActionEnum.HTTP_UPDATE_BUTTON_THEME_SETTINGS).switchMap(() => {
    return service.siteService.updateButtonThemeSettings(
      state.configuration.settings.buttonThemeId,
    );
  });
};

export default {
  actions: [
    ping$,
    getTemplateById$,
    getWebDocByHash$,
    getTemplateMetadata$,
    getPageBySlug$,
    getHomePage$,
    httpSavePage$,
    getAllPages$,
    httpWarmUpCache$,
    httpUploadImage$,
    httpUploadFile$,
    httpDeleteImage$,
    httpDeleteFile$,
    httpUpdatePage$,
    httpUpdateTemplate$,
    httpDeletePage$,
    httpCreateFolder$,
    httpDeleteFolder$,
    httpMediaLibraryRename$,
    httpGetMediaLibraryImages$,
    httpGetMedialibraryFolders$,
    httpMoveImage$,
    httpRenameImage$,
    getDesignObjectMetadata$,
    submitForm$,
    resolveLinkedData$,
    httpUpdateFonts$,
    httpUpdateLogos$,
    httpUpdateFonts$,
    httpUpdateColors$,
    httpGetLinkedInSettings$,
    httpUpdateAdminSettings$,
    httpUploadFavIcon$,
    httpUpdateCookiePolicySettings$,
    httpUpdateSpinnerSettings$,
    httpUpdateMainMenuSettings$,
    httpUpdateButtonThemeSettings$,
    httpUpdateGlobalErrorSettings$,
    httpUpdateTagManagerSettings$,
    httpUpdateCookiePolicies$,
  ],
};
